import { SITE_FACTS } from "@shared/siteFacts";
import { useRef, useCallback, useEffect } from "react";
import { AssistantCard } from "@/components/AssistantCard";
import { recordSearchClick } from "@/lib/searchTelemetry";
import { useLocation } from "wouter";
import { getCategoryById, getToolById } from "@/lib/tools-data";
import {
  useSearchPanelState,
  closeSearchPanel,
  setSearchPanelWidth,
  updateSearchPanelQuery,
  setSearchPanelQueryText,
  beginSearchPanelSearch,
  finishSearchPanelSearch,
  failSearchPanelSearch,
  cancelSearchPanelSearch,
} from "@/hooks/useSearchPanel";
import { showUpdateBanner } from "@/hooks/useUpdateBanner";
import { Route, Search, HelpCircle, User, BookOpen, X } from "lucide-react";
import { parsePipelineRequest } from "@/lib/pipelineParser";

/**
 * The desktop-only, docked search panel described and approved via
 * mockup: a fixed right-edge panel (not a modal -- the rest of the
 * page stays fully visible and interactive underneath), draggable
 * narrower/wider from its left edge, closed only by the × or Escape,
 * never by clicking elsewhere on the page.
 *
 * Two real, confirmed bugs fixed after live testing surfaced them
 * directly, not from re-reading requirements: the site's own header is
 * `sticky top-0 z-50` at 64px tall, which sat ON TOP of this panel's
 * own top-0 z-40 header bar (the query text and × button), making
 * both unreadable/unclickable -- this panel now starts below that
 * header (top-16) instead of overlapping it, and its own z-index no
 * longer needs to compete with the site header's since they no longer
 * occupy the same vertical space. Separately, the panel was a pure
 * overlay covering whatever page content happened to sit under its
 * width (a homepage card, in the case that surfaced this) rather than
 * the page actually making room for it -- Layout.tsx's <main> now
 * reserves space equal to the panel's width whenever it's open, so the
 * page genuinely reflows instead of being covered.
 *
 * The query is now a real, editable input INSIDE this panel (not a
 * read-only echo of whatever the header/hero search box held) -- this
 * is the direct fix for the reported "search is gone after clicking a
 * result" problem: since this panel is mounted once, globally, and
 * persists across navigation (unlike the header/hero inputs' own
 * page-local state, which resets on every route change), typing here
 * keeps working after opening a tool, letting someone refine their
 * search or pick a different result without starting over. Auto-
 * focuses this input the moment the panel opens, so continuing to type
 * after the header/hero box's first couple of characters naturally
 * lands here instead.
 *
 * Reuses the exact same result-grouping logic and priority order as
 * SearchResultsDropdown (multi-step pipeline detection first when the
 * query matches one, then tools, then For Your Work / personas, then
 * blog, then FAQ) rather than inventing a second, potentially-
 * inconsistent presentation -- this panel and that dropdown are two
 * different shells around the same underlying search results.
 *
 * The empty/loading area at the top is deliberately left as a single
 * spot to extend from later: an AI-generated answer summarizing or
 * synthesizing across the matched results (mentioned as a planned
 * next step) would render there, above the existing grouped list,
 * without needing to restructure this panel's layout.
 */
export function SearchSidePanel() {
  const { isOpen, query, results, submittedQuery, isSearching, width } = useSearchPanelState();
  const [, navigate] = useLocation();
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isResizingRef = useRef(false);

  const { tools, faq, personas, blog, assistant, noMatch } = results;
  // Derived from the submitted query, not the live text: the multi-step
  // suggestion used to be computed on every keystroke, so it would
  // propose a "plan" for half-typed input.
  // Suppressed whenever the AI already answered (any kind -- clarify,
  // answer, or workflow): this is a plain keyword guess with no awareness
  // of what the AI decided, and it was rendering ABOVE the AI's own
  // workflow answer with none of its nuance (e.g. an assumption the AI
  // named, like an unspecified file type) -- confirmed directly as a real
  // duplicate-and-bury problem, not assumed. The AI's workflow result
  // covers the same "this needs several tools" case through its own
  // privileged slot (AssistantCard, kind "workflow"), so there is nothing
  // for this guess to add once that is present.
  const pipelineMatch =
    !assistant && !noMatch && submittedQuery.trim().length >= 6 ? parsePipelineRequest(submittedQuery) : null;
  const hasResults = tools.length > 0 || faq.length > 0 || personas.length > 0 || blog.length > 0 || !!pipelineMatch || !!assistant;

  useEffect(() => {
    if (!isOpen) return;
    inputRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeSearchPanel();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  // Runs only on Enter -- see Layout.tsx's SearchBar for why search no
  // longer runs while typing. Results are published through the store
  // and never write the query text back (finishSearchPanelSearch), which
  // was the cause of "the box won't let me type": a slow result landing
  // mid-keystroke replaced newer text with the older query it started
  // from.
  const submitSearch = useCallback((raw: string) => {
    const value = raw.trim();
    if (value.length < 2) return;
    const token = beginSearchPanelSearch(value);
    import("@/lib/knowledgeBaseSearch")
      .then(async ({ searchKnowledgeBase }) => {
        const r = await searchKnowledgeBase(value);
        finishSearchPanelSearch(token, value, r);
      })
      .catch(() => {
        failSearchPanelSearch(token);
        showUpdateBanner();
      });
  }, []);

  const handleInputChange = useCallback((value: string) => {
    // Always synchronous, and nothing else happens while typing.
    setSearchPanelQueryText(value);
    if (value.trim().length < 2) {
      cancelSearchPanelSearch();
      updateSearchPanelQuery(value, { tools: [], faq: [], personas: [], blog: [], topResult: null });
    }
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
    e.preventDefault();
    const value = query.trim();
    if (value.length < 2) return;
    // Results already shown for exactly this text: Enter opens the best
    // match; otherwise Enter runs the search.
    if (value === submittedQuery && !isSearching && results.topResult) {
      recordSearchClick(results.topResult.href, "enter", 0);
      navigate(results.topResult.href);
      return;
    }
    submitSearch(value);
  };

  const startResize = useCallback((e: React.PointerEvent) => {
    isResizingRef.current = true;
    const startX = e.clientX;
    const startWidth = panelRef.current?.getBoundingClientRect().width ?? width;
    const onMove = (moveEvent: PointerEvent) => {
      if (!isResizingRef.current) return;
      // Dragging the LEFT edge left (negative delta) should widen a
      // right-docked panel, so the sign is inverted from a naive
      // "current minus start" delta.
      const delta = startX - moveEvent.clientX;
      setSearchPanelWidth(startWidth + delta);
    };
    const onUp = () => {
      isResizingRef.current = false;
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  }, [width]);

  if (!isOpen) return null;

  const goToTool = (toolId: string, position: number) => {
    recordSearchClick(`/tool/${toolId}`, "tool", position);
    navigate(`/tool/${toolId}`);
  };
  const goToPipeline = () => {
    if (!pipelineMatch) return;
    const idsParam = pipelineMatch.map((m) => m.tool.id).join(",");
    recordSearchClick(`/pipeline?tools=${encodeURIComponent(idsParam)}`, "pipeline", 0);
    navigate(`/pipeline?tools=${encodeURIComponent(idsParam)}`);
  };

  return (
    <div
      ref={panelRef}
      role="complementary"
      aria-label="Search results"
      className="fixed top-16 right-0 z-40 h-[calc(100vh-4rem)] bg-popover border-l border-border shadow-2xl hidden md:flex flex-col"
      style={{ width }}
    >
      {/* Resize handle */}
      <div
        onPointerDown={startResize}
        className="absolute left-0 top-0 h-full w-1.5 -translate-x-1/2 cursor-ew-resize group flex items-center justify-center"
        title="Drag to resize"
      >
        <div className="w-1 h-8 rounded-full bg-border group-hover:bg-primary/50 transition-colors" />
      </div>

      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border shrink-0">
        <Search className="h-4 w-4 text-muted-foreground shrink-0" />
        <input
          ref={inputRef}
          aria-label="Search tools"
          enterKeyHint="search"
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Search ${SITE_FACTS.toolCountLabel} tools... (press Enter)`}
          className="flex-1 min-w-0 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
        <button
          type="button"
          onClick={closeSearchPanel}
          aria-label="Close search panel"
          className="shrink-0 rounded-md p-1 hover:bg-accent transition-colors"
        >
          <X className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isSearching && (
          <div className="px-4 py-3 text-sm text-muted-foreground">Searching...</div>
        )}

        {!isSearching && submittedQuery && query.trim() !== submittedQuery && (
          <p className="px-4 py-2 text-xs text-muted-foreground border-b border-border/60">
            Showing results for "{submittedQuery}" — press Enter to search for what you've typed.
          </p>
        )}

        {!isSearching && !submittedQuery && (
          <div className="px-4 py-8 text-center">
            <Search className="h-4 w-4 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Type what you're trying to do, then press Enter.</p>
          </div>
        )}

        {!isSearching && submittedQuery && !hasResults && (
          <div className="px-4 py-8 text-center">
            <Search className="h-4 w-4 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No matches for "{submittedQuery}"</p>
          </div>
        )}

        {assistant && (
          <AssistantCard
            assistant={assistant}
            onNavigate={(href, kind, position) => {
              recordSearchClick(href, kind, position);
              navigate(href);
            }}
          />
        )}

        {pipelineMatch && (
          <div className="px-4 py-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wide mb-2">
              <Route className="h-3.5 w-3.5" />
              This looks like a multi-step request
            </div>
            <p className="text-sm mb-1.5">To do this, you'd need {pipelineMatch.length} tools in sequence:</p>
            <ol className="text-sm mb-2 pl-5 list-decimal space-y-0.5">
              {pipelineMatch.map((m) => (
                <li key={m.tool.id}>{m.tool.name}</li>
              ))}
            </ol>
            <button type="button" onClick={goToPipeline} className="text-sm font-medium text-primary hover:underline">
              Yes, guide me through it &rarr;
            </button>
          </div>
        )}

        {noMatch && !isSearching && (
          <p className="px-4 pt-3 pb-1 text-sm font-medium text-foreground" role="status">
            No tool matches &ldquo;{submittedQuery}&rdquo;
          {/shorten|short\s*-?link|short\s*url|url\s*shortener/i.test(submittedQuery) && (
            <span className="block mt-1 text-xs font-normal text-muted-foreground">The URL shortener was retired. These are the closest tools.</span>
          )}
          </p>
        )}

        {tools.length > 0 && (
          <div>
            <p className={`px-4 pt-3 pb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide ${pipelineMatch ? "border-t border-border/60" : ""}`}>
              {noMatch ? "Closest tools" : "Tools"}
            </p>
            {tools.map((doc, index) => {
              const toolId = doc.id.replace(/^tool-/, "");
              const tool = getToolById(toolId);
              const category = tool ? getCategoryById(tool.category) : undefined;
              const Icon = category?.icon;
              // See SearchResultsDropdown.tsx's matching comment -- not
              // every `tools` entry is a real, existing tool (the AI-assist
              // path can prepend a synthetic single-tool or workflow
              // result), and goToTool always assumes /tool/${toolId} is
              // right, so anything without a real tool behind it falls
              // back to doc.href.
              const handleClick = tool
                ? () => goToTool(toolId, index)
                : () => {
                    recordSearchClick(doc.href, doc.href.startsWith("/pipeline") ? "pipeline" : "tool", index);
                    navigate(doc.href);
                  };
              return (
                <button
                  key={doc.id}
                  className="w-full px-4 py-2.5 text-left hover:bg-accent transition-colors flex items-start gap-3"
                  onClick={handleClick}
                >
                  {Icon && <Icon className="h-4 w-4 shrink-0 mt-0.5" style={{ color: category?.color }} />}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{doc.title}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{doc.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {personas.length > 0 && (
          <div>
            <p className={`px-4 pt-3 pb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide ${tools.length > 0 || pipelineMatch ? "border-t border-border/60" : ""}`}>
              For Your Work
            </p>
            {personas.map((doc, i) => (
              <button
                key={doc.id}
                className="w-full px-4 py-2.5 text-left hover:bg-accent transition-colors flex items-start gap-3"
                onClick={() => { recordSearchClick(doc.href, "persona", i); navigate(doc.href); }}
              >
                <User className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{doc.title}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">{doc.description}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {blog.length > 0 && (
          <div>
            <p className={`px-4 pt-3 pb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide ${tools.length > 0 || pipelineMatch || personas.length > 0 ? "border-t border-border/60" : ""}`}>
              Guides &amp; Articles
            </p>
            {blog.map((doc, i) => (
              <button
                key={doc.id}
                className="w-full px-4 py-2.5 text-left hover:bg-accent transition-colors flex items-start gap-3"
                onClick={() => { recordSearchClick(doc.href, "blog", i); navigate(doc.href); }}
              >
                <BookOpen className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{doc.title}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">{doc.description}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {faq.length > 0 && (
          <div>
            <p className={`px-4 pt-3 pb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide ${tools.length > 0 || pipelineMatch || personas.length > 0 || blog.length > 0 ? "border-t border-border/60" : ""}`}>
              Good to know
            </p>
            {faq.map((doc) => (
              <div key={doc.id} className="px-4 py-2.5 flex items-start gap-3">
                <HelpCircle className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{doc.title}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">{doc.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
